import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Box,
  Typography,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Print as PrintIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Remove as RemoveIcon,
  FileDownload as DownloadIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';

const SalesTable = ({
  invoices,
  page,
  rowsPerPage,
  totalInvoices,
  onPageChange,
  onRowsPerPageChange,
  onViewInvoice,
  onPrintInvoice,
  onConvertDraftInvoice,
  onDeleteInvoice,
  onPayBalance,
  onDownloadInvoice,
  onResubmitZatca,
  resubmittingInvoiceId,
  activeTab,
  phase2Enabled = false
}) => {
  const [downloadAnchorEl, setDownloadAnchorEl] = React.useState(null);
  const [activeDownloadInvoice, setActiveDownloadInvoice] = React.useState(null);

  const handleDownloadClick = (event, invoice) => {
    setDownloadAnchorEl(event.currentTarget);
    setActiveDownloadInvoice(invoice);
  };

  const handleDownloadClose = () => {
    setDownloadAnchorEl(null);
    setActiveDownloadInvoice(null);
  };

  const handleDownloadOption = (format) => {
    if (activeDownloadInvoice && onDownloadInvoice) {
      onDownloadInvoice(activeDownloadInvoice, format);
    }
    handleDownloadClose();
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'hold':
        return 'info';
      default:
        return 'default';
    }
  };

  const renderZatcaStatus = (zatcaStatus) => {
    if (!zatcaStatus || zatcaStatus === '') {
      return (
        <Tooltip title="Not submitted to ZATCA">
          <RemoveIcon fontSize="small" sx={{ color: 'text.disabled' }} />
        </Tooltip>
      );
    }

    if (['CLEARED', 'REPORTED', 'SUCCESS'].includes(zatcaStatus)) {
      return (
        <Tooltip title={`Submitted: ${zatcaStatus}`}>
          <CheckCircleIcon
            fontSize="small"
            sx={{
              color: '#4caf50',
              '&:hover': { color: '#45a049' }
            }}
          />
        </Tooltip>
      );
    }

    return (
      <Tooltip title={`Failed: ${zatcaStatus}`}>
        <CancelIcon
          fontSize="small"
          sx={{
            color: '#f44336',
            '&:hover': { color: '#d32f2f' }
          }}
        />
      </Tooltip>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        overflow: 'hidden'
      }}
    >
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{
                fontWeight: 400,
                color: 'text.secondary',
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Invoice #</TableCell>

              <TableCell sx={{
                fontWeight: 400,
                color: 'text.secondary',
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Date</TableCell>
              <TableCell sx={{
                fontWeight: 400,
                color: 'text.secondary',
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Customer</TableCell>
              <TableCell sx={{
                fontWeight: 400,
                color: 'text.secondary',
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Customer Type</TableCell>
              <TableCell sx={{
                fontWeight: 400,
                color: 'text.secondary',
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Mobile No</TableCell>
              <TableCell align="right" sx={{
                fontWeight: 400,
                color: 'text.secondary',
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Total Amount</TableCell>
              <TableCell align="right" sx={{
                fontWeight: 400,
                color: 'text.secondary',
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>VAT Amount</TableCell>
              <TableCell align="right" sx={{
                fontWeight: 400,
                color: 'text.secondary',
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Grand Total</TableCell>
              <TableCell align="right" sx={{
                fontWeight: 400,
                color: 'text.secondary',
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Balance</TableCell>
              <TableCell align="center" sx={{
                fontWeight: 400,
                color: 'text.secondary',
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Status</TableCell>
              {phase2Enabled && activeTab === 0 && (
                <TableCell align="center" sx={{
                  fontWeight: 400,
                  color: 'text.secondary',
                  py: 1.5,
                  borderBottom: '2px solid',
                  borderBottomColor: 'divider'
                }}>ZATCA</TableCell>
              )}
              <TableCell align="center" sx={{
                fontWeight: 400,
                color: 'text.secondary',
                py: 1.5,
                borderBottom: '2px solid',
                borderBottomColor: 'divider'
              }}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {invoices.length > 0 ? (
              invoices.map((invoice) => (
                <TableRow
                  key={invoice.id}
                  hover
                  sx={{
                    '&:last-child td': { borderBottom: 0 },
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" color="primary" fontWeight="medium">
                      {invoice.id}
                    </Typography>
                  </TableCell>

                  <TableCell>{invoice.date}</TableCell>
                  <TableCell>{invoice.customer}</TableCell>
                  <TableCell>{invoice.customerType || '-'}</TableCell>
                  <TableCell>{invoice.mobileNo}</TableCell>
                  <TableCell align="right">{Number(invoice.totalAmount).toFixed(2)}</TableCell>
                  <TableCell align="right">{Number(invoice.vatAmount).toFixed(2)}</TableCell>
                  <TableCell align="right">{Number(invoice.grandTotal).toFixed(2)}</TableCell>
                  <TableCell align="right">
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      color={invoice.balanceAmount > 0 ? 'error.main' : 'success.main'}
                    >
                      {Number(invoice.balanceAmount || 0).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={invoice.paymentStatus}
                      color={getStatusColor(invoice.paymentStatus)}
                      size="small"
                      sx={{
                        fontWeight: 500,
                        minWidth: '80px'
                      }}
                    />
                  </TableCell>
                  {phase2Enabled && activeTab === 0 && (
                    <TableCell align="center">
                      {renderZatcaStatus(invoice.zatcaStatus)}
                    </TableCell>
                  )}
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => onViewInvoice(invoice)}
                        sx={{ color: 'primary.main' }}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => onPrintInvoice(invoice)}
                        sx={{ color: 'text.secondary' }}
                      >
                        <PrintIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => handleDownloadClick(e, invoice)}
                        sx={{ color: 'primary.main' }}
                        title="Download"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                      {phase2Enabled && activeTab === 0 && !['CLEARED', 'REPORTED', 'SUCCESS'].includes(invoice.zatcaStatus) && (
                        <Tooltip title="Resubmit to ZATCA">
                          <IconButton
                            size="small"
                            onClick={() => onResubmitZatca(invoice)}
                            disabled={resubmittingInvoiceId === (invoice.invoiceNo || invoice.id)}
                            sx={{ color: 'warning.main' }}
                          >
                            {resubmittingInvoiceId === (invoice.invoiceNo || invoice.id) ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <SyncIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                      {activeTab === 0 && invoice.balanceAmount > 0 && (
                        <IconButton
                          size="small"
                          onClick={() => onPayBalance(invoice)}
                          sx={{ color: 'success.main' }}
                          title="Pay Balance"
                        >
                          <PaymentIcon fontSize="small" />
                        </IconButton>
                      )}
                      {activeTab === 1 && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => onConvertDraftInvoice(invoice)}
                            sx={{ color: 'success.main' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => onDeleteInvoice(invoice)}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={phase2Enabled && activeTab === 0 ? 12 : 11} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {activeTab === 0 ? 'No invoices found' : 'No draft invoices available'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={totalInvoices}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        sx={{
          borderTop: '1px solid',
          borderTopColor: 'divider',
          '& .MuiTablePagination-select': {
            py: 0.5,
          },
          '& .MuiTablePagination-displayedRows': {
            fontSize: '0.875rem',
          }
        }}
      />
      <Menu
        anchorEl={downloadAnchorEl}
        open={Boolean(downloadAnchorEl)}
        onClose={handleDownloadClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* <MenuItem onClick={() => handleDownloadOption('xml')}>XML</MenuItem>                                                                                                     */}
        <MenuItem onClick={() => handleDownloadOption('thermal')}>Thermal Print</MenuItem>
        <MenuItem onClick={() => handleDownloadOption('a4')}>A3 PDF</MenuItem>
      </Menu>
    </Paper>
  );
};

export default React.memo(SalesTable); 